(() => {
  if (customElements.get('brg-bundle')) return;
  const products = new Map();
  const englishProducts = new Map();
  const dependentWheels = new Set(["logitech-g29-g923-f1-open-wheel-mod", "f1-open-wheel-mod-v2-for-logitech-g29-g923-playstation-black-version", "logitech-g920-f1-open-wheel-mod", "f1-open-wheel-mod-v2-for-logitech-g920-xbox", "logitec-g923xbox-f1-open-wheel-mod", "f1-open-wheel-mod-v2-for-logitech-g923-xbox"]);
  const money = (cents, locale, currency) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  const totals = (rows, tiers) => {
    const selected = rows.filter(row => row.selected);
    const tier = [...tiers].sort((a, b) => a.min_qty - b.min_qty).filter(t => selected.length >= t.min_qty).pop();
    const percent = tier ? Number(tier.discount_amount) : 0;
    const subtotal = selected.reduce((sum, row) => sum + row.variant.price, 0);
    const saving = selected.reduce((sum, row) => sum + Math.round(row.variant.price * percent / 100), 0);
    return { count: selected.length, percent, subtotal, saving, total: subtotal - saving };
  };
  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  class BRGBundle extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.kind = this.config.kind || 'wheel';
      this.dataset.kind = this.kind;
      this.text = this.config.text;
      this.fallback = structuredClone(this.config);
      this.mode = this.dataset.context;
      this.root = this.dataset.root.replace(/\/?$/, '/');
      this.format = cents => money(cents, this.dataset.locale, this.dataset.currency);
      this.wheelHandles = this.config.wheelHandles.split(/[\s,]+/).filter(Boolean);
      this.configuredOfferIds = [...new Set([this.config.offerId, ...(this.config.offerIds || [])].filter(Boolean))];
      this.addEventListener('change', event => event.stopPropagation());
      if (this.mode === 'product') {
        this.productScope = this.closest('.shopify-section') || this.closest('section');
        this.variantListener = event => {
          if (event.target !== this.productPicker()) return;
          this.hasSeenVariant = true;
          this.syncPrimary(event.detail.variant);
        };
        this.productScope?.addEventListener('brg:variant-change', this.variantListener);
        this.readPrimary();
      }
      this.appListener = () => this.syncApp();
      document.addEventListener('zrx-loaded', this.appListener);
      document.addEventListener('visibilitychange', this.appListener);
      if (this.mode === 'cart' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.load());
      }
      this.syncApp(false);
      await this.load();
      this.syncApp();
      let attempts = 0;
      this.poll = setInterval(() => {
        this.syncApp();
        if (++attempts >= 30) clearInterval(this.poll);
      }, 1000);
    }
    disconnectedCallback() {
      clearInterval(this.poll);
      this.unsubscribe?.();
      this.productScope?.removeEventListener('brg:variant-change', this.variantListener);
      document.removeEventListener('zrx-loaded', this.appListener);
      document.removeEventListener('visibilitychange', this.appListener);
      this.hiddenStyle?.remove();
      this.observer?.disconnect();
    }
    productPicker() {
      return [...(this.productScope?.querySelectorAll('variant-selects, variant-radios') || [])]
        .find(picker => picker.dataset.url?.split('/').filter(Boolean).pop() === this.config.currentHandle);
    }
    readPrimary() {
      const picker = this.productPicker();
      if (!picker || typeof picker.getVariantData !== 'function') return;
      if (this.hasSeenVariant) { this.syncPrimary(picker.currentVariant); return; }
      if (!picker.variantData && !picker.querySelector('[type="application/json"]')) return;
      const form = document.getElementById(`product-form-${picker.dataset.section}`);
      const id = Number(form?.querySelector('input[name="id"]')?.value);
      const variant = picker.currentVariant || picker.getVariantData().find(item => item.id === id);
      if (variant) this.syncPrimary(variant);
    }
    syncPrimary(variant) {
      this.config.currentVariant = variant?.id || 0;
      this.primaryUnavailable = !variant || !variant.available;
      const row = this.rows?.find(item => item.primary);
      const target = row?.product.variants.find(item => item.id === variant?.id);
      if (row && target) {
        row.variant = target;
        this.renderOptions(row, 0);
      }
      if (row && variant && !target) this.primaryUnavailable = true;
      this.update();
    }
    selectPrimary(row, variant) {
      const picker = this.productPicker();
      if (!picker) { this.config.currentVariant = variant.id; return; }
      // Match by option name: colour swatches can appear after the dropdowns.
      row.product.options.forEach((option, index) => {
        const name = typeof option === 'string' ? option : option.name;
        picker.querySelectorAll('select').forEach(select => {
          if (select.name !== `options[${name}]`) return;
          select.value = variant.options[index];
          select.dispatchEvent(new Event('change'));
        });
        picker.querySelectorAll('input[type="radio"]').forEach(input => {
          if (input.name === name || input.name.startsWith(`${name}-`)) {
            input.checked = input.value === variant.options[index];
          }
        });
      });
      picker.dispatchEvent(new Event('change', { bubbles: true }));
    }
    async getProduct(handle) {
      const key = this.root + handle;
      if (!products.has(key)) products.set(key, fetch(`${this.root}products/${encodeURIComponent(handle)}.js`).then(response => {
        if (!response.ok) throw new Error('Product unavailable');
        return response.json();
      }).catch(error => { products.delete(key); throw error; }));
      return products.get(key);
    }
    async getEnglishProduct(handle) {
      if ((this.dataset.locale || '').toLowerCase().startsWith('en')) return this.getProduct(handle);
      if (!englishProducts.has(handle)) englishProducts.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      }).then(response => {
        if (!response.ok) throw new Error('English product unavailable');
        return response.json();
      }).catch(error => { englishProducts.delete(handle); throw error; }));
      return englishProducts.get(handle);
    }
    optionValues(product, optionIndex) {
      const values = [];
      (product?.variants || []).forEach(variant => {
        const value = variant?.options?.[optionIndex];
        if (value != null && !values.includes(value)) values.push(value);
      });
      return values;
    }
    englishOptionName(row, optionIndex) {
      const option = row.englishProduct?.options?.[optionIndex];
      return (typeof option === 'string' ? option : option?.name) || (typeof row.product.options?.[optionIndex] === 'string' ? row.product.options[optionIndex] : row.product.options?.[optionIndex]?.name);
    }
    englishOptionValue(row, optionIndex, localizedValue) {
      const localValues = this.optionValues(row.product, optionIndex);
      const englishValues = this.optionValues(row.englishProduct || row.product, optionIndex);
      const index = localValues.indexOf(localizedValue);
      return index >= 0 && englishValues[index] != null ? englishValues[index] : localizedValue;
    }
    async cart() {
      const response = await fetch(`${this.root}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    }
    syncApp(reload = true) {
      if (this.busy) return;
      const data = window.zoorix_data?.mix_and_match;
      if (!data?.config?.values || !Array.isArray(data.offers)) return;
      if (this.kind === 'wheel') {
        const offers = this.configuredOfferIds.map(id => data.offers.find(item => item.id === id))
          .filter(offer => offer && data.config.values.is_enabled && offer.enabled && !offer.preview_mode && data.config.values.method === 'product_discount_api')
          .map(offer => {
            const groups = offer.offer?.product_groups;
            const discount = offer.offer?.discount;
            if (!Array.isArray(groups) || groups.length !== 2 || groups.some(group => group.group_type !== 'products' || !Array.isArray(group.products)) || groups[0].min_qty !== 1 || groups[1].min_qty !== 0 || discount?.type !== 'tiered') return null;
            const tiers = discount.tiers;
            if (!tiers?.length || tiers.some(t => t.discount_type !== 'percentage' || !Number.isFinite(+t.discount_amount) || +t.discount_amount < 0 || +t.discount_amount > 100)) return null;
            const wheels = groups[0].products.map(p => p.handle).filter(Boolean);
            const accessories = groups[1].products.map(p => p.handle).filter(Boolean);
            if (!wheels.length || accessories.length !== 3) return null;
            return { id: offer.id, wheels, accessories, tiers };
          }).filter(Boolean);
        const signature = JSON.stringify(offers);
        if (signature === this.appSignature) return;
        this.appSignature = signature;
        this.activeWheelOffers = offers;
        this.wheelHandles = [...new Set(offers.flatMap(offer => offer.wheels))];
        if (!offers.length) return this.hide();
        const primary = this.rows?.find(row => row.primary);
        const current = offers.find(offer => offer.wheels.includes(primary?.product.handle));
        const sameProducts = current && this.rows.length === current.accessories.length + 1 && this.rows.slice(1).every(row => current.accessories.includes(row.product.handle));
        if (sameProducts) {
          this.config.offerId = current.id;
          this.config.accessories = current.accessories;
          this.config.tiers = current.tiers;
          this.renderTiers();
          this.update();
          this.hideLegacy();
        } else if (reload) {
          this.hide();
          this.load();
        }
        return;
      }
      const offer = data.offers.find(item => item.id === this.config.offerId);
      if (!offer) return;
      if (!data.config.values.is_enabled || !offer.enabled || offer.preview_mode || data.config.values.method !== 'product_discount_api') {
        this.appSignature = null;
        this.hidden = true;
        this.hiddenStyle?.remove();
        this.hiddenStyle = null;
        return;
      }
      const groups = offer.offer?.product_groups;
      const discount = offer.offer?.discount;
      if (this.kind === 'shifter') {
        if (!Array.isArray(groups) || groups.some(group => group.group_type !== 'products') || discount?.type !== 'percentage') return;
        const handles = [...new Set(groups.flatMap(group => group.products.map(p => p.handle)))];
        const amount = Number(discount.amount);
        if (handles.length !== 2 || !handles.every(h => this.wheelHandles.includes(h)) || !Number.isFinite(amount) || amount < 0 || amount > 100) return;
        this.config.tiers = [{ min_qty: 2, discount_amount: amount }];
        if (this.rows) { this.renderTiers(); this.update(); }
        return;
      }

    }
    async load() {
      const generation = this.generation = (this.generation || 0) + 1;
      try {
        let handle = this.config.currentHandle;
        let cart = null;
        if (this.mode === 'cart') {
          cart = await this.cart();
          this.dataset.currency = cart.currency;
          const wheels = cart.items.filter(item => this.wheelHandles.includes(item.handle));
          if (wheels.length !== 1 || wheels[0].quantity !== 1) return this.hide();
          if (this.kind === 'shifter') this.config.accessories = this.wheelHandles.filter(h => h !== wheels[0].handle);
          handle = wheels[0].handle;
          if (cart.items.some(item => this.config.accessories.includes(item.handle) && item.quantity !== 1)) return this.hide();
          if (this.config.accessories.some(h => cart.items.filter(item => item.handle === h).length > 1)) return this.hide();
        }
        if (!this.wheelHandles.includes(handle)) return this.hide();
        if (this.kind === 'wheel' && this.activeWheelOffers) {
          const offer = this.activeWheelOffers.find(item => item.wheels.includes(handle));
          if (!offer) return this.hide();
          this.config.offerId = offer.id;
          this.config.accessories = offer.accessories;
          this.config.tiers = offer.tiers;
        }
        if (this.kind === 'shifter') this.config.accessories = this.wheelHandles.filter(h => h !== handle);
        const handles = [handle, ...this.config.accessories];
        const list = await Promise.all(handles.map(h => this.getProduct(h)));
        const englishList = await Promise.all(handles.map((h, index) => this.getEnglishProduct(h).catch(() => list[index])));
        if (generation !== this.generation || !this.isConnected) return;
        this.rows = list.map((product, index) => {
          const existing = cart?.items.find(item => item.product_id === product.id);
          const selectedId = existing?.variant_id || (index === 0 ? this.config.currentVariant : 0);
          const preferred = !existing && product.handle === 'logitech-g29-g920-g923-g27-g25-brake-pedal-mod'
            ? product.variants.filter(v => v.available && /\b5\s+ELASTOMERS\b/i.test(v.title)).sort((a, b) => b.price - a.price)[0]
            : null;
          const variant = product.variants.find(v => v.id === Number(selectedId)) || preferred || product.variants.find(v => v.available) || product.variants[0];
          return { product, englishProduct: englishList[index] || product, variant, selected: index === 0 || Boolean(existing), existing: Boolean(existing), primary: index === 0 };
        });
        if (this.mode === 'cart' && this.rows.every(row => row.existing)) return this.hide();
        this.render();
        this.hidden = false;
        this.hideLegacy();
      } catch (error) {
        this.hide();
      }
    }
    hide() {
      this.generation = (this.generation || 0) + 1;
      this.hidden = true;
      this.hiddenStyle?.remove();
      this.hiddenStyle = null;
    }
    hideLegacy() {
      if (this.hiddenStyle?.isConnected && this.hiddenOfferId === this.config.offerId) return;
      this.hiddenStyle?.remove();
      this.hiddenOfferId = this.config.offerId;
      this.hiddenStyle = document.createElement('style');
      const selectors = [];
      const ids = (this.config.legacyIds || '').split(/[\s,]+/).filter(Boolean);
      ids.forEach(id => selectors.push(`[data-bundle-id="${CSS.escape(id)}"]`));
      if (this.rows?.length >= 2) selectors.push('[data-bundle-id]' + this.rows.map(row => `:has([data-product-id="${row.product.id}"])`).join(''));
      selectors.push(`.zrx-mix-match-offer[data-offer-id="${CSS.escape(this.config.offerId)}"]`, `[zoorix-offer-id="${CSS.escape(this.config.offerId)}"]`);
      this.hiddenStyle.textContent = selectors.join(',') + '{display:none!important}';
      document.head.append(this.hiddenStyle);
    }
    render() {
      this.querySelector('.brg-bundle-panel')?.remove();
      const panel = el('section', 'brg-bundle-panel');
      const heading = el('h2', 'brg-bundle-title', this.mode === 'cart' ? this.text.cartTitle : this.text.title);
      panel.append(heading, el('p', 'brg-bundle-intro', this.text.intro));
      this.tierList = el('div', 'brg-bundle-tiers');
      panel.append(this.tierList);
      this.list = el('div', 'brg-bundle-list');
      this.rows.forEach((row, index) => this.list.append(this.renderRow(row, index)));
      panel.append(this.list);
      this.summary = el('div', 'brg-bundle-summary');
      this.summary.setAttribute('aria-live', 'polite');
      this.summary.setAttribute('aria-atomic', 'true');
      panel.append(this.summary);
      this.button = el('button', 'brg-bundle-submit');
      this.button.type = 'button';
      this.button.addEventListener('click', () => this.submit());
      this.status = el('p', 'brg-bundle-status');
      this.status.setAttribute('role', 'status');
      this.status.hidden = true;
      panel.append(this.button, el('p', 'brg-bundle-note', this.text.notice), this.status);
      this.append(panel);
      this.renderTiers();
      this.update();
    }
    renderTiers() {
      if (!this.tierList) return;
      this.tierList.replaceChildren();
      [...this.config.tiers].sort((a, b) => a.min_qty - b.min_qty).forEach(tier => {
        const item = el('div', 'brg-bundle-tier');
        item.dataset.count = tier.min_qty;
        item.append(el('span', '', `${tier.min_qty} ${this.text.items}`), el('strong', '', `${tier.discount_amount}%`));
        this.tierList.append(item);
      });
    }
    renderRow(row, index) {
      const card = el('article', 'brg-bundle-row');
      row.node = card;
      const image = el('img', 'brg-bundle-image');
      image.width = 88; image.height = 88; image.loading = 'lazy'; image.alt = row.product.title;
      row.image = image;
      const body = el('div', 'brg-bundle-body');
      const title = el('a', 'brg-bundle-name', row.product.title);
      title.href = `${this.root}products/${encodeURIComponent(row.product.handle)}`;
      body.append(title);
      const meta = el('div', 'brg-bundle-meta');
      row.price = el('strong', 'brg-bundle-price');
      meta.append(row.price);
      if (row.existing || row.primary) meta.append(el('span', 'brg-bundle-tag', row.existing ? this.text.inCart : this.text.required));
      body.append(meta);
      row.options = el('div', 'brg-bundle-options');
      body.append(row.options);
      const toggle = el('label', 'brg-bundle-toggle');
      if (!row.primary && !row.existing) {
        const checkbox = el('input'); checkbox.type = 'checkbox'; checkbox.checked = row.selected;
        checkbox.setAttribute('aria-label', `${this.text.include}: ${row.product.title}`);
        checkbox.addEventListener('change', () => { row.selected = checkbox.checked; this.update(); });
        toggle.append(checkbox, el('span', 'brg-bundle-toggle-icon'));
      } else toggle.append(el('span', 'brg-bundle-check', '✓'));
      card.append(image, body, toggle);
      this.renderOptions(row, index);
      return card;
    }
    renderOptions(row, index) {
      row.options.replaceChildren();
      if (row.existing) {
        const englishVariant = row.englishProduct?.variants?.find(variant => variant.id === row.variant.id);
        const variantTitle = englishVariant?.title || row.variant.title;
        if (variantTitle !== 'Default Title') row.options.append(el('span', 'brg-bundle-variant-text', variantTitle));
        return;
      }
      if (row.product.variants.length === 1 && row.variant.title === 'Default Title') return;
      row.product.options.forEach((option, optionIndex) => {
        const name = this.englishOptionName(row, optionIndex);
        const selectedDisplay = this.englishOptionValue(row, optionIndex, row.variant.options[optionIndex]);
        const group = el('details', 'brg-bundle-option');
        const trigger = el('summary');
        trigger.append(el('span', 'brg-bundle-option-label', name), el('span', 'brg-bundle-option-value', selectedDisplay));
        const menu = el('div', 'brg-bundle-option-menu');
        const candidates = dependentWheels.has(row.product.handle) ? row.product.variants.filter(v => v.options.slice(0, optionIndex).every((part, i) => part === row.variant.options[i])) : row.product.variants;
        const values = [...new Set(candidates.map(v => v.options[optionIndex]))];
        values.forEach(value => {
          const englishValue = this.englishOptionValue(row, optionIndex, value);
          const choice = el('button', 'brg-bundle-choice', englishValue); choice.type = 'button';
          const matching = row.product.variants.filter(v => v.options[optionIndex] === value && v.options.slice(0, optionIndex).every((part, i) => part === row.variant.options[i]));
          choice.disabled = !matching.some(v => v.available);
          if (choice.disabled && dependentWheels.has(row.product.handle)) choice.textContent = `${englishValue} · ${window.variantStrings.soldOut}`;
          choice.setAttribute('aria-pressed', String(row.variant.options[optionIndex] === value));
          choice.addEventListener('click', () => {
            const target = matching.find(v => v.available && v.options.every((part, i) => i === optionIndex || part === row.variant.options[i])) || matching.find(v => v.available);
            if (!target) return;
            row.variant = target;
            if (row.primary && this.mode === 'product') this.selectPrimary(row, target);
            this.renderOptions(row, index);
            row.options.querySelectorAll('summary')[optionIndex]?.focus();
            this.update();
          });
          menu.append(choice);
        });
        group.append(trigger, menu);
        group.addEventListener('toggle', () => {
          if (group.open) this.querySelectorAll('details[open]').forEach(other => { if (other !== group) other.open = false; });
        });
        group.addEventListener('keydown', event => { if (event.key === 'Escape') { group.open = false; trigger.focus(); } });
        group.addEventListener('focusout', () => { setTimeout(() => { if (!group.contains(document.activeElement)) group.open = false; }, 0); });
        row.options.append(group);
      });
    }
    update() {
      if (!this.summary) return;
      this.list.inert = Boolean(this.busy);
      this.rows.forEach(row => {
        row.node.dataset.selected = row.selected;
        row.price.textContent = this.format(row.variant.price);
        const source = row.variant.featured_image?.src || row.product.featured_image || row.product.images[0];
        if (source) {
          const url = new URL(source, location.origin); url.searchParams.set('width', '176');
          row.image.src = url.href;
        }
      });
      this.result = totals(this.rows, this.config.tiers);
      const { count, percent, subtotal, saving, total } = this.result;
      this.tierList.querySelectorAll('.brg-bundle-tier').forEach(tier => {
        const active = Number(tier.dataset.count) === Math.min(count, 4);
        tier.classList.toggle('is-active', active);
      });
      const label = el('div', 'brg-bundle-total-label', `${this.text.total} · ${count} ${this.text.items}`);
      const prices = el('div', 'brg-bundle-total-prices');
      if (saving > 0) prices.append(el('s', '', this.format(subtotal)));
      prices.append(el('strong', '', this.format(total)));
      this.summary.replaceChildren(label, prices);
      if (saving > 0) this.summary.append(el('span', 'brg-bundle-saving', `${this.text.save} ${this.format(saving)} (${percent}%)`));
      const addedRows = this.rows.filter(row => row.selected && !row.existing);
      const unavailable = (this.mode === 'product' && this.primaryUnavailable) || addedRows.some(row => !row.variant.available);
      this.button.disabled = this.busy || count < 2 || addedRows.length === 0 || unavailable;
      this.button.textContent = this.busy ? this.text.adding : unavailable ? this.text.unavailable : count < 2 || !addedRows.length ? this.text.select : this.mode === 'cart' ? this.text.addMissing : this.text.add;
    }
    async submit() {
      if (this.mode === 'product') this.readPrimary();
      if (this.hidden || this.button.disabled || this.busy) return;
      this.busy = true; this.status.hidden = true; this.update();
      let added = false;
      try {
        if (this.mode === 'cart') {
          const fresh = await this.cart();
          const expected = this.rows.filter(row => row.existing);
          if (expected.some(row => !fresh.items.some(item => item.variant_id === row.variant.id && item.quantity === 1)) || this.rows.some(row => !row.existing && fresh.items.some(item => item.product_id === row.product.id))) {
            await this.load(); return;
          }
        }
        const rows = this.rows.filter(row => row.selected && !row.existing);
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: rows.map(row => ({ id: row.variant.id, quantity: 1 })) })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(typeof error.description === 'string' ? error.description : this.text.error);
        }
        added = true;
        const actual = await this.cart();
        const selected = this.rows.filter(row => row.selected);
        const matching = selected.map(row => actual.items.find(item => item.variant_id === row.variant.id));
        const actualTotal = matching.every(Boolean) ? matching.reduce((sum, item) => sum + item.final_price, 0) : -1;
        if (Math.abs(actualTotal - this.result.total) > selected.length) {
          this.status.replaceChildren(document.createTextNode(this.text.check + ' '));
          const link = el('a', '', this.text.openCart); link.href = this.dataset.cartUrl;
          this.status.append(link); this.status.hidden = false;
          this.button.disabled = true; this.button.hidden = true;
          return;
        }
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.status.textContent = added ? this.text.check : error.message || this.text.error;
        if (added) { const link = el('a', '', this.text.openCart); link.href = this.dataset.cartUrl; this.status.append(' ', link); this.button.hidden = true; }
        this.status.hidden = false;
      } finally {
        this.busy = false;
        this.update();
      }
    }
  }
  customElements.define('brg-bundle', BRGBundle);
})();
